import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useCalendarEvent } from '../hooks/useCalendar';
import { calendarService, CreateCommentDto, CalendarEventFile } from '../services/calendar.service';
import { usersService } from '@/features/users/services/users.service';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MessageSquare, Paperclip, ExternalLink, User, Video, Calendar as CalendarIcon, Upload, AlertCircle, Download, FolderOpen, Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

interface EventModalProps {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// key por evento: rascunho de comentário/notificações não vaza para outro evento.
export function EventModal(props: EventModalProps) {
  return <EventModalInner key={props.eventId} {...props} />;
}

function EventModalInner({ eventId, open, onOpenChange }: EventModalProps) {
  const { toast } = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [commentContent, setCommentContent] = useState('');
  const [selectedNotifyUsers, setSelectedNotifyUsers] = useState<string[]>([]);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [isUploadingDirecionamento, setIsUploadingDirecionamento] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: event, isLoading } = useCalendarEvent(eventId);

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await usersService.getAll();
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  const users = usersData || [];

  // Verificar se é evento de Captação (antes de qualquer return)
  const isCaptacao = event?.isCaptacao || false;
  const hasDirecionamento = event?.hasDirecionamento || false;

  // Buscar arquivos de direcionamento (sempre chamar o hook, mesmo que desabilitado)
  const { data: direcionamentoFiles = [], refetch: refetchDirecionamento } = useQuery({
    queryKey: ['calendar-event-direcionamento', eventId],
    queryFn: () => calendarService.getDirecionamentoFiles(eventId),
    enabled: isCaptacao && open && !!event,
  });

  const uploadDirecionamentoMutation = useMutation({
    mutationFn: (file: File) => calendarService.uploadDirecionamento(eventId, file),
    onSuccess: () => {
      toast({
        title: 'Arquivo enviado',
        description: 'O arquivo de direcionamento foi enviado com sucesso.',
      });
      refetchDirecionamento();
      queryClient.invalidateQueries({ queryKey: ['calendar-event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['calendar-dashboard-events'] });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao enviar arquivo',
        description: error?.response?.data?.message || 'Não foi possível enviar o arquivo.',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setIsUploadingDirecionamento(false);
    },
  });

  const createCommentMutation = useMutation({
    mutationFn: (dto: CreateCommentDto) => calendarService.createComment(eventId, dto),
    onSuccess: () => {
      toast({
        title: 'Comentário adicionado',
        description: 'O comentário foi adicionado com sucesso.',
      });
      setCommentContent('');
      setSelectedNotifyUsers([]);
      setShowCommentForm(false);
      queryClient.invalidateQueries({ queryKey: ['calendar-event', eventId] });
    },
  });

  const markCommentsAsReadMutation = useMutation({
    mutationFn: () => calendarService.markCommentsAsRead(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-event', eventId] });
    },
  });

  const handleAddComment = () => {
    if (!commentContent.trim()) {
      toast({
        title: 'Erro',
        description: 'O comentário não pode estar vazio.',
        variant: 'destructive',
      });
      return;
    }

    createCommentMutation.mutate({
      content: commentContent,
      notifyUserIds: selectedNotifyUsers.length > 0 ? selectedNotifyUsers : undefined,
    });
  };

  // Marcar comentários como lidos ao abrir o modal
  useEffect(() => {
    if (open && event?.comments) {
      const unreadComments = event.comments.filter((c) => !c.isRead);
      if (unreadComments.length > 0) {
        markCommentsAsReadMutation.mutate();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation muda a cada render e dispararia o efeito em loop
  }, [open, event?.comments]);

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="xl" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Evento</DialogTitle>
          <p className="text-muted-foreground">Carregando...</p>
        </DialogContent>
      </Dialog>
    );
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploadingDirecionamento(true);
      uploadDirecionamentoMutation.mutate(file);
    }
  };

  const unreadCommentsCount = event?.comments?.filter((c) => !c.isRead).length || 0;
  const needsDirecionamento = isCaptacao && !hasDirecionamento;

  if (!event) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-2xl">{event.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Informações básicas */}
          <div className="space-y-2">
            <div>
              <strong>Data/Hora:</strong>{' '}
              {format(new Date(event.startTime), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
                locale: ptBR,
              })}
              {' - '}
              {format(new Date(event.endTime), 'HH:mm', { locale: ptBR })}
            </div>
            {event.location && (
              <div>
                <strong>Local:</strong> {event.location}
              </div>
            )}
            {event.description && (
              <div>
                <strong>Descrição:</strong>
                <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">
                  {event.description}
                </p>
              </div>
            )}
            {event.meetLink && (
              <div>
                <strong className="flex items-center gap-2">
                  <Video className="h-4 w-4" />
                  Google Meet:
                </strong>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => window.open(event.meetLink ?? undefined, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Entrar na reunião
                </Button>
              </div>
            )}
            {event.htmlLink && (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(event.htmlLink ?? undefined, '_blank')}
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Abrir no Google Calendar
                </Button>
              </div>
            )}
            {event.calendar && (
              <div>
                <strong>Calendário:</strong>{' '}
                <span
                  className="inline-block w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: event.calendar.color || '#039be5' }}
                />
                {event.calendar.name}
              </div>
            )}
          </div>

          {/* Participantes */}
          {event.attendees && event.attendees.length > 0 && (
            <div>
              <strong className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Participantes:
              </strong>
              <div className="mt-2 space-y-1">
                {event.attendees.map((attendee, idx) => (
                  <div key={idx} className="text-sm">
                    {attendee.email} ({attendee.responseStatus || 'needsAction'})
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Direcionamento (apenas para eventos de Captação) */}
          {isCaptacao && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <strong className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Direcionamento
                  {needsDirecionamento && (
                    <span className="flex items-center gap-1 text-red-600 dark:text-red-400 text-sm font-normal">
                      <AlertCircle className="h-3 w-3" />
                      Falta direcionamento
                    </span>
                  )}
                </strong>
                <div>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    onChange={handleFileSelect}
                    disabled={isUploadingDirecionamento}
                    className="hidden"
                    id="direcionamento-file"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingDirecionamento}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {isUploadingDirecionamento ? 'Enviando...' : 'Enviar Arquivo'}
                  </Button>
                </div>
              </div>
              {direcionamentoFiles.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {direcionamentoFiles.map((file: CalendarEventFile) => {
                    const handleDownload = async () => {
                      try {
                        const blobData = await calendarService.downloadDirecionamentoFile(
                          eventId,
                          file.id,
                        );
                        const blob = new Blob([blobData]);
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = file.fileName;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                        toast({
                          title: 'Download iniciado',
                          description: 'O arquivo está sendo baixado.',
                        });
                      } catch (error: any) {
                        toast({
                          title: 'Erro ao baixar arquivo',
                          description: error?.response?.data?.message || 'Não foi possível baixar o arquivo.',
                          variant: 'destructive',
                        });
                      }
                    };

                    const handleDelete = async () => {
                      const ok = await confirm({
                        title: 'Remover arquivo?',
                        description: `O arquivo "${file.fileName}" será removido do direcionamento.`,
                        confirmLabel: 'Remover arquivo',
                        destructive: true,
                      });
                      if (!ok) return;

                      try {
                        await calendarService.deleteDirecionamentoFile(eventId, file.id);
                        toast({
                          title: 'Arquivo deletado',
                          description: 'O arquivo foi removido com sucesso.',
                        });
                        refetchDirecionamento();
                        queryClient.invalidateQueries({ queryKey: ['calendar-event', eventId] });
                        queryClient.invalidateQueries({ queryKey: ['calendar-dashboard-events'] });
                      } catch (error: any) {
                        toast({
                          title: 'Erro ao deletar arquivo',
                          description: error?.response?.data?.message || 'Não foi possível deletar o arquivo.',
                          variant: 'destructive',
                        });
                      }
                    };

                    return (
                      <div key={file.id} className="flex items-center justify-between p-2 border rounded">
                        <div className="flex-1">
                          <div className="font-medium">{file.fileName}</div>
                          <div className="text-xs text-muted-foreground">
                            {(file.fileSize / 1024).toFixed(2)} KB
                            {file.uploader && ` - Enviado por ${file.uploader.email}`}
                          </div>
                          {file.folderPath && (
                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                              📁 Localização: {file.folderPath}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={handleDownload}>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDelete}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remover
                          </Button>
                          {file.webViewLink && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(file.webViewLink, '_blank')}
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Abrir Arquivo
                            </Button>
                          )}
                          {file.folderWebViewLink && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(file.folderWebViewLink!, '_blank')}
                            >
                              <FolderOpen className="h-4 w-4 mr-2" />
                              Abrir Pasta
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mt-2">
                  Nenhum arquivo de direcionamento enviado.
                </p>
              )}
            </div>
          )}

          {/* Arquivos */}
          {event.files && event.files.length > 0 && (
            <div>
              <strong className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Arquivos:
              </strong>
              <div className="mt-2 space-y-2">
                {event.files.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-2 border rounded">
                    <div>
                      <div className="font-medium">{file.fileName}</div>
                      <div className="text-xs text-muted-foreground">
                        {(file.fileSize / 1024).toFixed(2)} KB
                        {file.uploader && ` - Enviado por ${file.uploader.email}`}
                      </div>
                    </div>
                    {file.webViewLink && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(file.webViewLink, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Abrir no Drive
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comentários */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <strong className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Comentários
                {unreadCommentsCount > 0 && (
                  <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                    {unreadCommentsCount} não lidos
                  </span>
                )}
              </strong>
              <Button variant="outline" size="sm" onClick={() => setShowCommentForm(!showCommentForm)}>
                {showCommentForm ? 'Cancelar' : 'Adicionar Comentário'}
              </Button>
            </div>

            {showCommentForm && (
              <div className="mb-4 p-4 border rounded-lg space-y-4">
                <Textarea
                  placeholder="Escreva um comentário..."
                  value={commentContent}
                  onChange={(e) => setCommentContent(e.target.value)}
                  rows={3}
                />
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Notificar usuários:
                  </label>
                  <Select
                    value={selectedNotifyUsers[0] || ''}
                    onValueChange={(value) => {
                      if (value && !selectedNotifyUsers.includes(value)) {
                        setSelectedNotifyUsers([...selectedNotifyUsers, value]);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione usuários para notificar" />
                    </SelectTrigger>
                    <SelectContent>
                      {users
                        .filter((u) => !selectedNotifyUsers.includes(u.id))
                        .map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.email}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {selectedNotifyUsers.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {selectedNotifyUsers.map((userId) => {
                        const user = users.find((u) => u.id === userId);
                        return (
                          <div key={userId} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={true}
                              onCheckedChange={() => {
                                setSelectedNotifyUsers(selectedNotifyUsers.filter((id) => id !== userId));
                              }}
                            />
                            {user?.email}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleAddComment}
                  disabled={createCommentMutation.isPending || !commentContent.trim()}
                >
                  {createCommentMutation.isPending ? 'Enviando...' : 'Enviar Comentário'}
                </Button>
              </div>
            )}

            <div className="space-y-3">
              {event.comments && event.comments.length > 0 ? (
                event.comments.map((comment) => (
                  <div
                    key={comment.id}
                    className={`p-3 border rounded-lg ${!comment.isRead ? 'bg-blue-50 border-blue-200' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {comment.user?.email || 'Usuário'}
                          {!comment.isRead && (
                            <span className="ml-2 text-xs text-blue-600">(não lido)</span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                          {comment.content}
                        </div>
                        <div className="text-xs text-muted-foreground mt-2 space-y-1">
                          <div>
                            <strong>Evento:</strong>{' '}
                            {format(new Date(event.startTime), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
                              locale: ptBR,
                            })}
                            {' - '}
                            {format(new Date(event.endTime), 'HH:mm', { locale: ptBR })}
                          </div>
                          <div>
                            <strong>Comentário criado em:</strong>{' '}
                            {format(new Date(comment.createdAt), "dd 'de' MMMM 'às' HH:mm", {
                              locale: ptBR,
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">Nenhum comentário ainda.</p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

